import { useCallback, useEffect, useRef, useState } from 'react'

import { useUnit } from 'effector-react'
import { twMerge } from 'tailwind-merge'

import ArrowLeftIcon from '@/assets/icons/icon-arrow-left.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import ArrowDownIcon from '@/assets/icons/icon-long-arrow-down.svg'
import OpenNewTabIcon from '@/assets/icons/icon-open-new-tab.svg'
import { LoadingBar } from '@/components/ui/LoadingBar.tsx'
import { AudioProvider } from '@/components/ui/audio-player/AudioProvider'
import { Button } from '@/components/ui/button.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $isAiChattingEnabled } from '@/models'
import {
  getSummaryFx,
  summarizeFx,
  trackSummaryProgressFx,
} from '@/models/chattingUI/chatSummary.ts'
import {
  $activeChatDetails,
  $fan,
  $fanId,
  scrollChatToBottomFx,
} from '@/models/chattingUI/chats/chats.ts'
import { $typingFanIds } from '@/models/chattingUI/chats/typing.ts'
import {
  $groupedMessagesEntries,
  $isNextPageFetchingError,
  $isPreviousPageFetchingError,
  $messageIdToFetch,
  $messages,
  $messagesError,
  getMessagesFx,
  getNextMessagePageFx,
  getPreviousMessagePageFx,
  resetMessagesByFanId,
  sendMessageFx,
  setMessageIdToFetch,
} from '@/models/chattingUI/messages/messages.ts'
import {
  $searchText,
  resetSearchText,
} from '@/models/chattingUI/messages/search.ts'
import { $isTranslatorEnabled } from '@/models/chattingUI/messages/translations.ts'
import { $unreadMessages } from '@/models/chattingUI/unreadChatMessages'
import { openDialog } from '@/models/dialogs'
import ActionAITranslationIcon from '@/modules/chatting/icons/action-ai-translation.svg'
import PinIcon from '@/modules/chatting/icons/icon-pin.svg'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider.ts'
import type { MessageSendRequest } from '@/modules/chatting/types.ts'
import { ChatMuteIndicator } from '@/modules/chatting/ui/chat-window/chat-mute-indicator.tsx'
import { MoreChatActions } from '@/modules/chatting/ui/chat-window/more-chat-actions.tsx'
import { ChatMessageGroup } from '@/modules/chatting/ui/components/ChatMessageGroup/ChatMessageGroup'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

import { useChatMessagesScrollListener } from '../../hooks/use-chat-messages-scroll-listener'
import DescriptionIcon from '../../icons/description.svg'
import EditIcon from '../../icons/edit.svg'
import GalleryIcon from '../../icons/gallery.svg'
import ReloadIcon from '../../icons/reload.svg'
import SearchIcon from '../../icons/search.svg'
import MinimizeIcon from '../../icons/sidebar-collapse.svg'
import ExpandIcon from '../../icons/sidebar-expand.svg'
import SummarizeIcon from '../../icons/summarize.svg'
import { MessagesSkeleton } from '../components/MessagesSkeleton'
import { IconButton } from '../components/icon-button'
import { VerticalSeparator } from '../components/vertical-separator'
import { ChatMessageSearch } from './chat-message-search'
import { ChatSuggestions } from './chat-suggestions'
import { ChattingMessageEditor } from './chatting-message-editor'
import { canNotSendReasonToMessage } from './constants'
import { unescapeHtmlText } from './fan-info/utils'
import { SaveToListAction } from './lists'
import { LanguageSelector } from './translation/language-selector'

interface Props {
  isFanInfoVisible: boolean
  toggleFanInfo: () => void
}

export const Chat = ({ isFanInfoVisible, toggleFanInfo }: Props) => {
  const aiChattingEnabled = useUnit($isAiChattingEnabled)
  const fanId = useUnit($fanId)
  const fan = useUnit($fan)
  const activeChat = useUnit($activeChatDetails)
  const unreadMessages = useUnit($unreadMessages)
  const unreadMessagesCount = unreadMessages[fanId]?.size
  const messagesError = useUnit($messagesError)
  const isNextPageFetchingError = useUnit($isNextPageFetchingError)
  const isPreviousPageFetchingError = useUnit($isPreviousPageFetchingError)
  const isFetchingPageMessages = useUnit(getMessagesFx.pending)
  const messages = useUnit($messages)
  const searchText = useUnit($searchText)
  const [scrolled, setScrolled] = useState(false)
  const [lastFanId, setLastFanId] = useState(fanId)

  const { withUser, canNotSendReason, canSendMessage } = activeChat ?? {}
  const { openNewTab, closeChatsWithUserId, isTabsLimitReached } =
    useChattingTabs()
  const { aiTranslationEnabled } = useSettingsContext()

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [isSearchbarVisible, setIsSearchbarVisible] = useState(false)
  const messageIdToFetch = useUnit($messageIdToFetch)
  const isChatSummarizing = useUnit(summarizeFx.pending)
  const isChatSummaryLoading = useUnit(getSummaryFx.pending)
  const isChatSummaryInProgress = useUnit(trackSummaryProgressFx.pending)
  const notAbleToSummarize =
    isChatSummarizing || isChatSummaryLoading || isChatSummaryInProgress

  const handleCloseSearch = useCallback(() => {
    setIsSearchbarVisible(false)
    resetSearchText()
    setMessageIdToFetch(null)
  }, [])

  useEffect(
    () => {
      setIsSearchbarVisible(false)
      resetSearchText()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fanId],
  )
  const typingFanIds = useUnit($typingFanIds)
  const isTranslatorEnabled = useUnit($isTranslatorEnabled)

  const isTyping = typingFanIds.includes(fanId)
  const groupedMessagesEntries = useUnit($groupedMessagesEntries)

  const handleScrollToEnd = useCallback(
    (smooth: unknown) => {
      if (isSearchbarVisible) {
        handleCloseSearch()
        resetMessagesByFanId({ fanId })
      }
      setScrolled(true)
      // smooth - could be event so in that case it should be true
      scrollChatToBottomFx(smooth !== false)
    },
    [fanId, handleCloseSearch, isSearchbarVisible],
  )

  const { highlightMessage } = useChatMessagesScrollListener({
    messagesContainerRef,
  })

  useEffect(() => {
    // here, and in effects below very complex logic about scrolling to a dedicated message, please pay a lot of attention if you will try to modify it
    // this happens only when messages already in chat
    if (!messageIdToFetch || isFetchingPageMessages) return
    const element = document.getElementById(
      `chatting-message-${messageIdToFetch}`,
    )

    element?.scrollIntoView({ behavior: 'instant', block: 'end' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageIdToFetch])

  useEffect(() => {
    // if we scroll down and messages updated - autoscroll it to the bottom
    if (
      !scrolled &&
      messages.length &&
      !isFetchingPageMessages &&
      !messageIdToFetch
    ) {
      handleScrollToEnd(false)
    }
  }, [
    handleScrollToEnd,
    messages,
    isFetchingPageMessages,
    messageIdToFetch,
    scrolled,
  ])

  useEffect(() => {
    // reset a scroll flag if no messages, to scroll to the bottom after update
    if (!messages.length) {
      setScrolled(!!messageIdToFetch)
    }
  }, [messageIdToFetch, messages.length])

  useEffect(() => {
    // if we switch fan, immediately scroll down to a latest message
    if (lastFanId !== fanId) {
      setLastFanId(fanId)
      setScrolled(!!messageIdToFetch)
    }
  }, [fanId, lastFanId, messageIdToFetch])

  const nameToShow = unescapeHtmlText(
    withUser?.displayName || withUser?.name || withUser?.username,
  )

  const handleSendMessage = useCallback(
    async (message: Partial<MessageSendRequest>) => {
      await sendMessageFx({
        userId: fanId,
        message,
        chat: activeChat,
        optimisticMessageId: (Math.random() * 10000000) >> 0,
      })
    },
    [activeChat, fanId],
  )

  const handleRetryMessagesLoad = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    isPreviousPageFetchingError
      ? getPreviousMessagePageFx()
      : getNextMessagePageFx()
  }, [isPreviousPageFetchingError])

  const handleChatClose = () => {
    closeChatsWithUserId(fanId)
  }

  const handleClickSummary = () => {
    openDialog({
      type: DIALOG_TYPE.SUMMARIZE_CHAT,
    })
  }

  const handleClickRenameUser = () => {
    openDialog({
      type: DIALOG_TYPE.RENAME_USER,
      user: withUser,
    })
  }

  const handleClickPin = () => {
    openDialog({
      type: DIALOG_TYPE.PINNED_MESSAGES,
      isPersistent: true,
    })
  }

  const handleClickAITranslationIcon = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.TRANSLATION_CONFIG_DIALOG,
    })
  }, [])

  const handleChatGalleryDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CHAT_GALLERY,
    })
  }, [])
  const handleClickEditNotice = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.NOTICE,
      notice: fan.notice ?? '',
      user: fan,
    })
  }, [fan])
  const handleOpenTab = useCallback(() => {
    const { id, name, fanNumber, avatar, displayName, username, avatarThumbs } =
      withUser

    openNewTab({
      fanId: id,
      fanNumber,
      avatar: avatarThumbs?.c50 ?? avatarThumbs?.c144 ?? avatar,
      title: displayName || name || username,
    })
  }, [openNewTab, withUser])
  const newMessageText =
    unreadMessagesCount > 1
      ? `${unreadMessagesCount} new messages`
      : `${unreadMessagesCount} new message`

  return (
    <div className="flex w-full flex-col overflow-hidden">
      <div className="bg-card flex items-end justify-between gap-4 border-b px-3 py-1.5 sm:px-6 sm:py-3">
        <div className="flex items-center gap-4">
          <button onClick={handleChatClose}>
            <ArrowLeftIcon className="text-secondary-300 h-5 w-5" />
          </button>
          <CreatorAvatar
            size={50}
            avatarSrc={
              withUser?.avatarThumbs?.c50 ??
              withUser?.avatarThumbs?.c144 ??
              withUser?.avatar
            }
            name={nameToShow ?? ''}
          />
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-lg">
              <span className="font-semibold">{nameToShow}</span>
              {withUser?.name &&
                unescapeHtmlText(withUser?.name) !== nameToShow && (
                  <span className="text-secondary-300">
                    ({unescapeHtmlText(withUser?.name)})
                  </span>
                )}
              <IconButton
                icon={EditIcon}
                onClick={handleClickRenameUser}
                tooltip="Edit name"
              />
            </div>
            <div className="text-secondary-300 flex items-center gap-2 2xl:gap-4">
              {withUser?.online && (
                <>
                  <div className="flex items-center gap-1">
                    <div className="bg-success-default h-2 w-2 rounded-full" />
                    <span className="text-success-default text-sm max-sm:hidden">
                      Online
                    </span>
                  </div>
                  <VerticalSeparator />
                </>
              )}
              <SaveToListAction />
              <ChatMuteIndicator />
              <IconButton
                icon={PinIcon}
                onClick={handleClickPin}
                tooltip="Pinned messages"
                iconClassName={classNames(
                  'h-3 w-3 sm:h-5 sm:w-5',
                  !!activeChat?.countPinnedMessages &&
                    'fill-primary-500 text-primary-500',
                )}
              />
              <VerticalSeparator />
              <IconButton
                icon={DescriptionIcon}
                onClick={handleClickEditNotice}
                tooltip="Edit notes"
                iconClassName="h-3 w-3 sm:h-5 sm:w-5"
              />
              <VerticalSeparator />
              <IconButton
                icon={GalleryIcon}
                tooltip="Open Gallery with User"
                iconClassName="h-3 w-3 sm:h-5 sm:w-5"
                label={<span className="hidden 2xl:inline-block">Gallery</span>}
                onClick={handleChatGalleryDialogOpen}
              />
              <VerticalSeparator />
              <IconButton
                icon={SearchIcon}
                iconClassName="h-3 w-3 sm:h-5 sm:w-5"
                label={<span className="hidden 2xl:inline-block">Find</span>}
                onClick={() => setIsSearchbarVisible((prev) => !prev)}
              />
              <VerticalSeparator />
              {aiTranslationEnabled && (
                <IconButton
                  tooltip="AI Language Translator"
                  icon={ActionAITranslationIcon}
                  onClick={handleClickAITranslationIcon}
                />
              )}
              {isTranslatorEnabled && (
                <>
                  <VerticalSeparator />
                  <LanguageSelector />
                </>
              )}
              <VerticalSeparator />
              <IconButton
                icon={SummarizeIcon}
                tooltip="Summarize Chat"
                iconClassName="h-3 w-3 sm:h-5 sm:w-5"
                onClick={handleClickSummary}
                disabled={notAbleToSummarize}
              />
            </div>
          </div>
        </div>
        <div className="text-title flex items-center gap-2 2xl:gap-4">
          <IconButton
            tooltip="Open Chat in New Tab"
            onClick={handleOpenTab}
            icon={OpenNewTabIcon}
            disabled={isTabsLimitReached}
            iconClassName="h-4 w-4 sm:h-5 sm:w-5"
          />
          <MoreChatActions />
          <IconButton
            icon={isFanInfoVisible ? ExpandIcon : MinimizeIcon}
            tooltip={(isFanInfoVisible ? 'Hide' : 'Show') + ' fan info'}
            onClick={toggleFanInfo}
            iconClassName="h-4 w-4 sm:h-5 sm:w-5"
          />
        </div>
      </div>
      {isFetchingPageMessages && <LoadingBar />}
      <div className="relative flex h-[calc(100vh-142px)] flex-col overflow-hidden">
        <ChatMessageSearch
          isVisible={isSearchbarVisible}
          onHide={handleCloseSearch}
        />
        {(isNextPageFetchingError || isPreviousPageFetchingError) && (
          <IconButton
            rounded
            icon={ReloadIcon}
            onClick={handleRetryMessagesLoad}
            className={classNames(
              'absolute left-1/2 z-10 -translate-x-1/2 bg-white',
              {
                'top-2 rotate-45': isNextPageFetchingError,
                'bottom-2 -rotate-90': isPreviousPageFetchingError,
              },
            )}
          />
        )}
        <div
          className="overflow-anchoring-auto relative flex-1 overflow-scroll px-6 py-2"
          ref={messagesContainerRef}
          id="chatting-messages-container"
        >
          {!messages.length && isFetchingPageMessages && <MessagesSkeleton />}
          {messagesError && !messagesError.direction && (
            <div className="flex h-full flex-col items-center justify-center">
              <p className="">Something went wrong while loading messages</p>
              <Button
                className="mt-2"
                onClick={handleRetryMessagesLoad}
              >
                Retry
              </Button>
            </div>
          )}
          <AudioProvider>
            {groupedMessagesEntries?.map(([date, msgs]) => (
              <ChatMessageGroup
                date={date}
                messages={msgs}
                key={date}
                highlightMessage={highlightMessage}
                searchedKeyword={searchText}
                searchedMessageId={messageIdToFetch}
              />
            ))}
          </AudioProvider>
          <div className="no-overflow-anchoring h-[1px]" />
        </div>
        <div
          className={twMerge(
            'absolute z-10 flex items-center justify-center bottom-0 left-0 w-full translate-y-full opacity-0 duration-300',
            unreadMessagesCount > 0 && 'opacity-100 translate-y-0',
          )}
        >
          <Button
            size="sm"
            className="p-2"
            onClick={handleScrollToEnd}
          >
            {newMessageText} <ArrowDownIcon />
          </Button>
        </div>
      </div>
      {isTyping && (
        <div className="flex items-center gap-1.5 px-6 text-xs">
          <CreatorAvatar
            size={20}
            avatarSrc={
              withUser?.avatarThumbs?.c50 ??
              withUser?.avatarThumbs?.c144 ??
              withUser?.avatar
            }
            name={nameToShow ?? ''}
          />
          is typing
        </div>
      )}
      <div className="relative mt-2 px-6 pb-6">
        {aiChattingEnabled && <ChatSuggestions />}
        {!aiChattingEnabled &&
          (canSendMessage ? (
            <ChattingMessageEditor
              userId={fanId}
              onSendMessage={handleSendMessage}
            />
          ) : (
            <div className="border-primary-100 bg-card dark:border-primary-800 relative rounded-[8px] border px-3 py-2.5">
              <div className="relative z-10 flex items-center">
                <InfoIcon className="text-primary-500 h-4 w-4" />
                <p className="ml-1.5 text-sm">
                  {canNotSendReason &&
                    canNotSendReasonToMessage[canNotSendReason]}
                </p>
              </div>
              <div className="bg-primary-50 dark:bg-primary-900 absolute left-0 top-0 h-full w-5 rounded-none rounded-bl-[8px] rounded-tl-[8px]" />
            </div>
          ))}
      </div>
    </div>
  )
}
